/**
 * @module Utils
 *
 * Academy trial onboarding events (Spec v2 §6). One track() choke point so
 * the base properties (user_id, account_id, trial_start_date,
 * days_since_trial_start, platform, session_id) land on every event;
 * per-event wrappers keep call sites typo-proof.
 */
import { AnalyticsService } from "app/services";
import { store } from "app/Store";
import { UserModel } from "app/models";
import { v4 as uuid } from "uuid";

export type SetupItemId =
  | "facility_profile"
  | "calendar_options"
  | "booking_options"
  | "stripe_connect"
  | "packages_subscriptions"
  | "coach_profile"
  | "academy_page"
  | "invite_athlete"
  | "mobile_app";

export type SetupPhaseId = "schedule" | "get_paid" | "get_customers";
export type CompletionSource = "checklist_cta" | "rail_cta" | "organic_nav";
/** How many of the nine items each completion_source accounted for. */
export interface CompletionSourceCounts {
  cta_completions: number;
  rail_completions: number;
  organic_completions: number;
}
/** Spec §6.2 collection methods: offline, online through CoachNow (Stripe), or both. */
export type BillingMethod = "in_person" | "coachnow" | "both";
export type BillingMethodSource = "checklist" | "settings";
export type WizardStepId = "facility_basics" | "coach_identity";

export const ANALYTICS_SESSION_ID_KEY = "academy-analytics-session-id";
const DAY_MS = 24 * 60 * 60 * 1000;

/** One id per browser tab; sessionStorage dies with the tab, so no cleanup needed. */
const sessionId = (): string | undefined => {
  try {
    let id = sessionStorage.getItem(ANALYTICS_SESSION_ID_KEY);
    if (!id) {
      id = uuid();
      sessionStorage.setItem(ANALYTICS_SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
};

/**
 * `trialStartedAt` is the trial start wherever a plan carries it, so it is
 * read first. Academy trial payloads omit it and carry only `activatedAt`,
 * which is the trial start while `status` is "trial" (past the trial the
 * activation date is the paid start, not a trial start). The account-level
 * subscription is the last fallback. Dates arrive as ISO strings despite the
 * `Date` type on the model.
 */
const trialStart = (self: UserModel | null | undefined): Date | undefined => {
  const plan = self?.plan;
  const raw =
    plan?.trialStartedAt ??
    (plan?.status === "trial" ? plan.activatedAt : undefined) ??
    self?.subscription?.trialStartedAt;
  if (!raw) {
    return undefined;
  }
  const date = new Date(raw);
  return isNaN(date.getTime()) ? undefined : date;
};

const baseProps = (): { [key: string]: any } => {
  const self = store.getState().user.self;
  const start = trialStart(self);
  return {
    user_id: self?.id,
    account_id:
      self?.organizationId ??
      self?.facilityMemberships?.[0]?.facilityId ??
      self?.scheduling?.facility?.id,
    trial_start_date: start && start.toISOString().slice(0, 10),
    days_since_trial_start: start && Math.max(0, Math.floor((Date.now() - start.getTime()) / DAY_MS)),
    platform: "web",
    session_id: sessionId(),
  };
};

const track = (name: string, props: { [key: string]: any }) => {
  AnalyticsService.track(name, { ...baseProps(), ...props });
};

export const trackSetupPageViewed = (
  itemsCompleteCount: number,
  entryPoint: "sidebar_pill" | "wizard_complete" | "rail" | "direct",
) =>
  track("setup_page_viewed", {
    items_complete_count: itemsCompleteCount,
    entry_point: entryPoint,
  });

export const trackChecklistItemClicked = (
  itemId: SetupItemId,
  phaseId: SetupPhaseId,
  itemPosition: number,
) =>
  track("setup_checklist_item_clicked", {
    item_id: itemId,
    phase_id: phaseId,
    item_position: itemPosition,
  });

/** `timeToCompleteMs` is measured from the launching CTA click; organic completions have none. */
export const trackChecklistItemCompleted = (
  itemId: SetupItemId,
  phaseId: SetupPhaseId,
  completionSource: CompletionSource,
  timeToCompleteMs?: number,
) =>
  track("setup_checklist_item_completed", {
    item_id: itemId,
    phase_id: phaseId,
    completion_source: completionSource,
    ...(timeToCompleteMs === undefined
      ? {}
      : { time_to_complete_ms: timeToCompleteMs }),
  });

/**
 * "Skip for now" on an optional item. Paired with the persisted
 * visited/skipped state, this is what product correlates against the real
 * completion flag (did they ever connect Stripe?) to weigh each step.
 */
export const trackChecklistItemSkipped = (
  itemId: SetupItemId,
  phaseId: SetupPhaseId,
  itemPosition: number,
) =>
  track("setup_checklist_item_skipped", {
    item_id: itemId,
    phase_id: phaseId,
    item_position: itemPosition,
  });

export const trackPhaseCompleted = (phaseId: SetupPhaseId) =>
  track("setup_phase_completed", { phase_id: phaseId });

export const trackChecklistCompleted = (counts: CompletionSourceCounts) =>
  track("setup_checklist_completed", {
    cta_completions: counts.cta_completions,
    rail_completions: counts.rail_completions,
    organic_completions: counts.organic_completions,
  });

/**
 * The coach's explicit collection-method choice. Product pinned that choice
 * to the payment guard's Confirm (plan blocker B2, 2026-09-15): no
 * billing-settings field exists, and the guard is single-select, so "both"
 * is never reported from the web app.
 */
export const trackBillingMethodSelected = (
  method: BillingMethod,
  source: BillingMethodSource,
) => track("billing_method_selected", { method, source });

export const trackWizardStarted = (
  entryPoint: "trial_signup" | "upgrade" | "other",
) => track("academy_wizard_started", { entry_point: entryPoint });

export const trackWizardStepViewed = (
  stepId: WizardStepId,
  stepIndex: number,
) =>
  track("academy_wizard_step_viewed", {
    step_id: stepId,
    step_index: stepIndex,
    total_steps: 2,
  });

export const trackWizardStepCompleted = (
  stepId: WizardStepId,
  stepIndex: number,
  timeOnStepMs: number,
  fieldsCompleted: number,
  fieldsPrefilled: number,
  fieldsEdited: number,
) =>
  track("academy_wizard_step_completed", {
    step_id: stepId,
    step_index: stepIndex,
    time_on_step_ms: timeOnStepMs,
    fields_completed: fieldsCompleted,
    fields_prefilled: fieldsPrefilled,
    fields_edited: fieldsEdited,
  });

export const trackWizardBackClicked = (
  fromStepId: WizardStepId,
  toStepId: WizardStepId,
) =>
  track("academy_wizard_back_clicked", {
    from_step_id: fromStepId,
    to_step_id: toStepId,
  });

export const trackWizardExited = (
  stepId: WizardStepId,
  stepIndex: number,
  exitType: "close_button" | "abandoned",
) =>
  track("academy_wizard_exited", {
    step_id: stepId,
    step_index: stepIndex,
    exit_type: exitType,
  });

export const trackWizardCompleted = (
  totalDurationMs: number,
  backClicksCount: number,
) =>
  track("academy_wizard_completed", {
    total_duration_ms: totalDurationMs,
    back_clicks_count: backClicksCount,
  });

export const trackRailShown = (
  completedItemId: SetupItemId,
  nextItemId: SetupItemId | null,
  screen: string,
) =>
  track("setup_rail_shown", {
    completed_item_id: completedItemId,
    next_item_id: nextItemId,
    screen,
  });

export const trackRailContinueClicked = (
  completedItemId: SetupItemId,
  nextItemId: SetupItemId,
) =>
  track("setup_rail_continue_clicked", {
    completed_item_id: completedItemId,
    next_item_id: nextItemId,
  });

export const trackRailBackClicked = (completedItemId: SetupItemId) =>
  track("setup_rail_back_clicked", { completed_item_id: completedItemId });

export const trackRailDismissed = (
  completedItemId: SetupItemId,
  dismissType: "explicit" | "navigation",
) =>
  track("setup_rail_dismissed", {
    completed_item_id: completedItemId,
    dismiss_type: dismissType,
  });

export type PaymentObjectType = "package" | "subscription" | "program";

export const trackPaymentFieldShown = (
  objectType: PaymentObjectType,
  stripeConnected: boolean,
) =>
  track("payment_method_field_shown", {
    object_type: objectType,
    stripe_connected: stripeConnected,
  });

export const trackPaymentMethodSelected = (
  objectType: PaymentObjectType,
  method: "online" | "offline",
  stripeConnected: boolean,
) =>
  track("payment_method_selected", {
    object_type: objectType,
    method,
    stripe_connected: stripeConnected,
  });

export const trackPaymentGuardStripeClicked = (objectType: PaymentObjectType) => track("payment_guard_stripe_clicked", { object_type: objectType });

export const trackDemoBannerClicked = (itemsCompleteCount: number) =>
  track("demo_banner_clicked", { items_complete_count: itemsCompleteCount });

export const trackAppDownloadCtaClicked = (itemsCompleteCount: number) =>
  track("app_download_cta_clicked", {
    items_complete_count: itemsCompleteCount,
  });
